import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const Benefits = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const benefits = [
    {
      icon: FiIcons.FiDollarSign,
      title: 'Save Money',
      description: 'Pay less for the same or better internet service. Our clients save an average of 40% on their monthly bills.',
      stats: '40% Average Savings'
    },
    {
      icon: FiIcons.FiClock,
      title: 'Save Time',
      description: 'No more hours spent researching providers and negotiating rates. We handle everything for you.',
      stats: '2+ Hours Saved'
    },
    {
      icon: FiIcons.FiUsers,
      title: 'Expert Guidance',
      description: 'Local experts who know the Hudson Valley market and have relationships with all major providers.',
      stats: '15+ Local Providers'
    },
    {
      icon: FiIcons.FiShield,
      title: 'Best Rates Guaranteed',
      description: 'We leverage our volume and relationships to get you better rates than going direct.',
      stats: 'Price Match Guarantee'
    }
  ];

  const process = [
    {
      step: '1',
      title: 'Free Analysis',
      description: 'We review your current bill and business needs to identify savings opportunities.'
    },
    {
      step: '2',
      title: 'Provider Shopping',
      description: 'We negotiate with all available providers in your area to find the best rates.'
    },
    {
      step: '3',
      title: 'Best Quote Presented',
      description: 'You receive the best offer with detailed comparison to your current service.'
    },
    {
      step: '4',
      title: 'Simple Switch',
      description: 'We handle the transition with minimal downtime and no contract headaches.'
    }
  ];

  return (
    <section id="benefits" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Why Choose{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-600">
              Clark-Ritchotte
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            We save you time and money by leveraging our expertise and provider relationships
          </p>
        </motion.div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {benefits.map((benefit, index) => (
            <motion.div
              key={benefit.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              className="bg-white rounded-xl p-6 text-center shadow-lg"
            >
              <div className="flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4 mx-auto">
                <SafeIcon icon={benefit.icon} className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">{benefit.title}</h3>
              <p className="text-gray-600 mb-4 text-sm">{benefit.description}</p>
              <div className="text-2xl font-bold text-blue-600">{benefit.stats}</div>
            </motion.div>
          ))}
        </div>

        {/* Process Timeline */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="bg-white rounded-2xl shadow-xl p-8 mb-16"
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">How It Works</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {process.map((item, index) => (
              <div key={index} className="text-center">
                <div className="flex items-center justify-center w-12 h-12 bg-blue-600 text-white rounded-full mb-4 mx-auto font-bold text-lg">
                  {item.step}
                </div>
                <h4 className="font-bold text-gray-900 mb-2">{item.title}</h4>
                <p className="text-sm text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="text-center"
        >
          <div className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl p-12 text-white">
            <h3 className="text-3xl font-bold mb-4">
              Ready to Start Saving?
            </h3>
            <p className="text-xl mb-8 opacity-90">
              Get your free consultation and see how much you can save on business internet
            </p>
            <button
              onClick={() => scrollToSection('contact')}
              className="bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1"
            >
              Start your Free Consultation
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Benefits;